import React, { useCallback, useEffect } from 'react';

import { WizardCardList } from 'src/app/components/lib/wizard/card';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import { DiscountAppliesTo } from '../../../models';
import { Filter } from '../../shared/filter';
import { isRewardsStepValid } from '../../shared/helpers';
import { useDiscountWizardStateContext } from '../../state';
import { RewardsStepActions } from '../../state/rewards';

import { AdditionalSettingsCard } from './additional-settings-card';
import { DiscountApplicationCard } from './discount-application-card';
import { ProductListCard } from './product-list-card';
import { RewardProductsCard } from './reward-products-card';

import type { WizardStepComponentProps } from 'src/app/components/lib/wizard';

export function Rewards({
  disableNextButton,
  enableNextButton,
  setComplete,
  setIncomplete,
  stepStatus,
  setSubstepActive,
  substepActive,
}: WizardStepComponentProps) {
  const markStepComplete = useCallback(() => {
    enableNextButton();
    setComplete();
  }, [enableNextButton, setComplete]);

  const markStepIncomplete = useCallback(() => {
    disableNextButton();
    setIncomplete();
  }, [disableNextButton, setIncomplete]);

  const { rewardsStepDispatch, rewardsStepState, isManualDiscount } = useDiscountWizardStateContext();
  const useDiscountsLoyaltyMultiplierFix = useLDFlag(LD.DISCOUNTS_LOYALTY_MULTIPLIER_FIX, false);

  useEffect(() => {
    const stepIsValid = isRewardsStepValid({ ...rewardsStepState, isManualDiscount }, useDiscountsLoyaltyMultiplierFix);
    if (!stepIsValid && stepStatus.complete) {
      markStepIncomplete();
    }
    if (stepIsValid && !stepStatus.complete) {
      markStepComplete();
    }
  }, [
    enableNextButton,
    isManualDiscount,
    markStepComplete,
    markStepIncomplete,
    rewardsStepState,
    setComplete,
    stepStatus,
    useDiscountsLoyaltyMultiplierFix,
  ]);

  const { discountAppliesTo } = rewardsStepState;

  const selectRewardProducts = discountAppliesTo === DiscountAppliesTo.DifferentProducts;

  useEffect(() => {
    rewardsStepDispatch({ type: RewardsStepActions.VISITED }, true);
  }, [rewardsStepDispatch]);

  // If screen is really wide, allow the content of the wizard to grow so there's less dead space - 1440px comes from the breakpoint in our design system for wide desktops (src/app/components/lib/global-styles/breakpoints.ts)
  const screenSizeAboveLargeBreakpoint = window.screen.availWidth > 1440;

  return (
    <>
      {substepActive && <Filter requirement='reward' />}
      {!substepActive && (
        <WizardCardList $maxWidth={screenSizeAboveLargeBreakpoint ? '1000px' : '850px'}>
          <DiscountApplicationCard
            markComplete={() => enableNextButton()}
            markStepIncomplete={() => disableNextButton()}
          />
          <AdditionalSettingsCard />
          {selectRewardProducts && <RewardProductsCard onEditFiltersClick={() => setSubstepActive()} />}
          {!selectRewardProducts && <ProductListCard />}
        </WizardCardList>
      )}
    </>
  );
}
